/**
 * Centralized API client that automatically includes JWT authentication headers
 */
import { API_URL } from '../types';

/**
 * Get authentication headers including JWT token from localStorage
 */
export const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/**
 * Authenticated fetch wrapper that automatically includes JWT token
 */
export const apiFetch = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  
  const headers = {
    ...getAuthHeaders(),
    ...(options.headers || {}),
  };

  return fetch(url, {
    ...options,
    headers,
  });
};

/**
 * Convenience method for GET requests
 */
export const apiGet = async (endpoint: string): Promise<Response> => {
  return apiFetch(endpoint, { method: 'GET' });
};

/**
 * Convenience method for POST requests
 */
export const apiPost = async (endpoint: string, body?: any): Promise<Response> => {
  return apiFetch(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * Convenience method for PUT requests
 */
export const apiPut = async (endpoint: string, body?: any): Promise<Response> => {
  return apiFetch(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * Convenience method for PATCH requests
 */
export const apiPatch = async (endpoint: string, body?: any): Promise<Response> => {
  return apiFetch(endpoint, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * Convenience method for DELETE requests
 */
export const apiDelete = async (endpoint: string): Promise<Response> => {
  return apiFetch(endpoint, { method: 'DELETE' });
};

/**
 * Download a file from an API endpoint (POST request)
 * Automatically triggers browser download with proper filename
 */
export const apiDownloadFile = async (endpoint: string, body?: any, defaultFilename: string = 'download'): Promise<void> => {
  const response = await apiFetch(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Failed to download file');
  }

  // Get the filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = defaultFilename;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^";\r\n]+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  // Create blob and trigger download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/**
 * Download a file from a GET endpoint with authentication
 * Automatically triggers browser download with proper filename
 */
export const apiDownloadFileGet = async (endpoint: string, defaultFilename: string = 'download'): Promise<void> => {
  const response = await apiGet(endpoint);

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Failed to download file');
  }

  // Get the filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = defaultFilename;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^";\r\n]+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  // Create blob and trigger download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};
